import type { Role } from '@/types/api';

export type TabId = 'remarks' | 'docs' | 'query' | 'integrity' | 'collegium' | 'compare' | 'settings';
const ALL_TABS: { id: TabId; label: string }[] = [
  { id: 'remarks',   label: 'Замечания' },
  { id: 'docs',      label: 'Документы' },
  { id: 'query',     label: 'Запрос' },
  { id: 'integrity', label: 'Целостность' },
  { id: 'collegium', label: 'Коллегия' },
  { id: 'compare',   label: 'Сравнение редакций' },
  { id: 'settings',  label: 'Настройки' },
];

export const ROLE_TABS: Record<Role, TabId[]> = {
  curator: ['remarks', 'docs', 'query', 'integrity', 'collegium', 'settings'],
  expert:  ['remarks', 'docs', 'query'],
  gip:     ['remarks', 'docs'], // GIP uses split-view, but на planar layout оставляем 2 таба
  admin:   ['remarks', 'docs', 'query', 'integrity', 'collegium', 'settings'],
  // Заказчик (решение владельца 2026-07-03): порядок = жизненный цикл приёмки
  // (документы → готовность → замечания → сравнение редакций → настройки);
  // Коллегия (АИ-консультация) заказчику не показывается.
  // Заказчик v2 (2026-07-06, PRD §16): дом — «Документация» (загрузка+готовность,
  // сравнение внутри). Замечания — окно-слой по кнопке «+ Замечание приёмки»,
  // проектировщик — чип в шапке. Верхняя вкладка одна.
  customer: ['docs'],
};

interface Props {
  active: TabId;
  onChange: (t: TabId) => void;
  role: Role;
}

// Ярлыки языком роли: заказчик-инвестор думает «пройду ли экспертизу»,
// а не «целостность» (утверждено 2026-07-02).
const CUSTOMER_TAB_LABELS: Partial<Record<TabId, string>> = {
  docs: 'Документация',
  remarks: 'Замечания',
};

/** Sticky tab-bar с role-aware visibility. */
export function TabBar({ active, onChange, role }: Props) {
  const visible = ROLE_TABS[role] || ROLE_TABS.curator;
  // Порядок задаёт ROLE_TABS (у заказчика он сценарный), а не ALL_TABS.
  const tabs = visible
    .map((id) => ALL_TABS.find((tt) => tt.id === id))
    .filter((tt): tt is { id: TabId; label: string } => !!tt)
    .map((t) => role === 'customer' && CUSTOMER_TAB_LABELS[t.id]
      ? { ...t, label: CUSTOMER_TAB_LABELS[t.id]! }
      : t);
  return (
    <nav className="sticky bg-white border-b border-border z-30" style={{ top: 48 }}>
      <div className="mx-auto flex items-end" style={{ maxWidth: 1280, paddingLeft: 24, paddingRight: 24, gap: 4 }}>
        {tabs.map((t) => {
          const isActive = active === t.id;
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => onChange(t.id)}
              className="transition-colors rounded-t-btn"
              style={{
                padding: '14px 16px',
                fontWeight: isActive ? 600 : 500,
                fontSize: 14,
                color: isActive ? '#0A1628' : '#6B7280',
                borderBottom: isActive ? '2px solid #F59E0B' : '2px solid transparent',
                marginBottom: -1,
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
              }}
              onMouseEnter={(e) => { if (!isActive) { e.currentTarget.style.color = '#0A1628'; e.currentTarget.style.background = '#F3F4F6'; } }}
              onMouseLeave={(e) => { if (!isActive) { e.currentTarget.style.color = '#6B7280'; e.currentTarget.style.background = 'transparent'; } }}
            >{t.label}</button>
          );
        })}
      </div>
    </nav>
  );
}
